import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

function logSelectorWarn(selector, parentSelector = '') {
  const inParentMessage = parentSelector && `in '${parentSelector}'`;
  // eslint-disable-next-line
  console.error(`Warning! The selector '${selector}' ${inParentMessage} was not found. Skipped!`);
}

function compareValues(value, comparedValue) {
  return typeof value === 'function' ? value(comparedValue) : value === comparedValue;
}

const selectors = {
  iframe: ({ app }, value) => compareValues(value, app.isIframe),
  partner: ({ partner: { data = {} } }, value) => {
    const isPartner = Boolean(data.partner_slug);
    const isObjValue = value && typeof value === 'object';
    const partnerSettings = data.settings || {};

    if (isPartner && isObjValue) {
      for (const [key, val] of Object.entries(value)) {
        const hasKeyInData = key in partnerSettings;
        const dataValue = hasKeyInData ? partnerSettings[key] : true;

        if (process.env.NODE_ENV !== 'production' && !hasKeyInData) {
          logSelectorWarn(key, 'partner');
        }

        const condition = compareValues(!!val, !!dataValue);

        if (!condition) {
          return false;
        }
      }

      return true;
    }

    return compareValues(isObjValue ? false : value, isPartner);
  },
  selector: (state, fn) => typeof fn === 'function' && !!fn(state),
};

function getSelector(props) {
  return state => {
    for (const [key, value] of Object.entries(props)) {
      const selector = selectors[key];
      const val = selector ? selector(state, value) : true;

      if (process.env.NODE_ENV !== 'production' && !selector) {
        logSelectorWarn(key);
      }

      if (!val) {
        return false;
      }
    }

    return true;
  };
}

export function useGuard(props = {}) {
  const selector = getSelector(props);

  return useSelector(selector);
}

export function withGuard(props = {}) {
  return Component => {
    const guard = useGuard(props);

    return <Component {...props} guard={guard} />;
  };
}

function Guard({ children, ...props }) {
  const guard = useGuard(props);

  return guard && (children || null);
}

Guard.propTypes = {
  children: PropTypes.node,
};

export default memo(Guard);
